import React, { createContext, useContext, useReducer, useCallback, ReactNode, useMemo } from 'react';

// State shape for a single column
interface ColumnFilterState {
  selected: string[];
  available: string[];
}

// Global state for Audit Dashboard Filters
interface AuditFilterState {
  columns: Record<string, ColumnFilterState>;
}

// Actions
type Action =
  | { type: 'SET_AVAILABLE'; key: string; items: string[] }
  | { type: 'SET_SELECTED'; key: string; selected: string[] }
  | { type: 'RESET_ALL' }
  | { type: 'RESET_COLUMN'; key: string };

interface AuditFilterContextType {
  state: AuditFilterState;
  setAvailable: (key: string, items: string[]) => void;
  setSelected: (key: string, selected: string[]) => void;
  resetAll: () => void;
  resetColumn: (key: string) => void;
}

const AuditFilterContext = createContext<AuditFilterContextType | undefined>(undefined);

const initialState: AuditFilterState = {
  columns: {},
};

function auditFilterReducer(state: AuditFilterState, action: Action): AuditFilterState {
  switch (action.type) {
    case 'SET_AVAILABLE': {
      const { key, items } = action;
      // Initialize or update available items
      const existing = state.columns[key] || { selected: [], available: [] };
      return {
        ...state,
        columns: {
          ...state.columns,
          [key]: {
            ...existing,
            available: items,
          },
        },
      };
    }
    case 'SET_SELECTED': {
      const { key, selected } = action;
      const existing = state.columns[key];
      // Create entry if it doesn't exist (robustness)
      if (!existing) {
        return {
          ...state,
          columns: {
            ...state.columns,
            [key]: { selected, available: [] },
          },
        };
      }
      return {
        ...state,
        columns: {
          ...state.columns,
          [key]: { ...existing, selected },
        },
      };
    }
    case 'RESET_ALL': {
      // Reset all columns to select all available items
      const newColumns: Record<string, ColumnFilterState> = {};
      Object.keys(state.columns).forEach((key) => {
        newColumns[key] = {
          available: state.columns[key].available,
          selected: state.columns[key].available, // Select All
        };
      });
      return {
        ...state,
        columns: newColumns,
      };
    }
    case 'RESET_COLUMN': {
      const { key } = action;
      const existing = state.columns[key];
      if (!existing) return state;
      return {
        ...state,
        columns: {
          ...state.columns,
          [key]: {
            ...existing,
            selected: existing.available, // Select All
          },
        },
      };
    }
    default:
      return state;
  }
}

export const AuditFilterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(auditFilterReducer, initialState);

  const setAvailable = useCallback((key: string, items: string[]) => {
    dispatch({ type: 'SET_AVAILABLE', key, items });
  }, []);

  const setSelected = useCallback((key: string, selected: string[]) => {
    dispatch({ type: 'SET_SELECTED', key, selected });
  }, []);

  const resetAll = useCallback(() => {
    dispatch({ type: 'RESET_ALL' });
  }, []);

  const resetColumn = useCallback((key: string) => {
    dispatch({ type: 'RESET_COLUMN', key });
  }, []);

  const value = useMemo(() => ({ state, setAvailable, setSelected, resetAll, resetColumn }), [state, setAvailable, setSelected, resetAll, resetColumn]);

  return <AuditFilterContext.Provider value={value}>{children}</AuditFilterContext.Provider>;
};

export const useAuditFilterContext = () => {
  const context = useContext(AuditFilterContext);
  if (!context) {
    throw new Error('useAuditFilterContext must be used within an AuditFilterProvider');
  }
  return context;
};
